import React from "react";
import { motion } from "framer-motion";
import { Search, Eye, Gavel, CreditCard, Truck } from "lucide-react";
import BuyerBenefits from "./buyer/BuyerBenefits";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

const BuyerJourneySection = () => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const sectionData = labels.menuPages?.howItWorks?.buyerJourneySection || {};
  const stepsData = sectionData.steps || [];

  const icons = [
    <Search className="w-6 h-6 text-blue-500" />,
    <Eye className="w-6 h-6 text-green-500" />,
    <Gavel className="w-6 h-6 text-purple-500" />,
    <CreditCard className="w-6 h-6 text-orange-500" />,
    <Truck className="w-6 h-6 text-red-500" />,
  ];

  const steps = stepsData.map((step: any, index: number) => ({
    icon: icons[index] || icons[0],
    title: step.title || "",
    description: step.description || "",
    details: step.details || "",
  }));

  const topSteps = steps.slice(0, 2);
  const bottomSteps = steps.slice(2);

  return (
    <section className="py-16 bg-gradient-to-b from-emerald-50 to-blue-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {sectionData.title || "För Dig Som Vill Köpa"}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {sectionData.description ||
              "Upptäck din nästa bil genom vårt transparenta auktionssystem. Kvalitetscertifierade fordon med fullständig trygghet."}
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          {/* Top row - 2 larger boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {topSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="flex flex-col"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 h-full flex flex-col"
                >
                  <div className="flex flex-col items-center text-center mb-4">
                    <div className="mb-3">{step.icon}</div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-700 mb-3 font-medium flex-1">
                    {step.description}
                  </p>
                  <p className="text-sm text-gray-600">{step.details}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Bottom row - 3 smaller boxes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {bottomSteps.map((step, index) => (
              <motion.div
                key={index + 2}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: (index + 2) * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="flex flex-col"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 h-full flex flex-col"
                >
                  <div className="flex flex-col items-center text-center mb-3">
                    <div className="mb-2">{step.icon}</div>
                    <h3 className="text-sm font-bold text-gray-900">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-700 mb-2 font-medium flex-1">
                    {step.description}
                  </p>
                  <p className="text-xs text-gray-600">{step.details}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuyerJourneySection;
